import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import Button from '../components/ui/Button';
import NetworkVisual from '../components/visuals/NetworkVisual';
import DialogueVisual from '../components/visuals/DialogueVisual';

const QuoraService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const includes = [
    {
      title: "Strategic Answer Placement",
      desc: "Expertly crafted answers placed within the most relevant and high-traffic questions.",
      icon: FiIcons.FiMessageSquare
    },
    {
      title: "High-Intent Targeting",
      desc: "We focus on topics where users are actively searching for solutions and recommendations.",
      icon: FiIcons.FiTarget
    },
    {
      title: "Natural Brand Mentions",
      desc: "Seamlessly integrating your brand into the discussion to provide genuine value.",
      icon: FiIcons.FiAward
    },
    {
      title: "Intent-Aligned Content",
      desc: "Answers designed to satisfy exactly what the user is looking for in that moment.",
      icon: FiIcons.FiSearch
    },
    {
      title: "Predictable Visibility",
      desc: "Structured campaigns built to ensure your brand maintains a consistent presence.",
      icon: FiIcons.FiLayers
    }
  ];

  const steps = [
    {
      num: "1",
      title: "Identify Questions",
      desc: "We find the top-ranking Quora threads for your target keywords and niche."
    },
    {
      num: "2",
      title: "Create & Position",
      desc: "Our team drafts expert answers that naturally position your brand as the solution."
    },
    {
      num: "3",
      title: "Publish & Monitor",
      desc: "We go live and track engagement, visibility, and traffic over the long term."
    }
  ];

  const benefits = [
    "Drive targeted traffic from relevant audiences",
    "Increase brand awareness and trust",
    "Capture long-tail search queries from Google",
    "Build credibility through helpful, expert content",
    "Support broader SEO and content strategies"
  ];

  return (
    <div className="bg-[#0B0F19] min-h-screen">
      {/* Hero Section - Centralized & H1 */}
      <section className="relative pt-40 pb-24 overflow-hidden border-b border-white/5">
        <NetworkVisual />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-7xl font-extrabold text-white mb-6 leading-tight tracking-tight"
            >
              Quora <span className="text-red-500">Services</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl md:text-2xl text-slate-400 mb-10 leading-relaxed max-w-2xl mx-auto"
            >
              Increase your brand visibility and capture targeted traffic by positioning your business within high-ranking Quora answers.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex justify-center w-full sm:w-auto"
            >
              <Button href="#/contact" variant="primary" className="!from-red-600 !to-rose-600 !px-12 !py-5 text-xl font-bold shadow-[0_0_40px_rgba(185,43,39,0.4)] w-full sm:w-auto">
                Start Reaching Audience
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-24">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-10 tracking-tight">Reach Users Through High-Intent Questions</h2>
            <div className="text-slate-400 space-y-8 text-xl leading-relaxed mx-auto">
              <p>
                Quora is a platform where users actively search for answers, recommendations, and solutions. Many Quora pages rank on Google, making it a powerful channel for reaching audiences with high intent.
              </p>
              <p>
                PRBoost helps businesses and agencies position their brand within relevant questions and discussions, ensuring your message appears where users are already looking.
              </p>
            </div>
          </div>

          {/* Service Includes */}
          <div className="max-w-6xl mx-auto mb-32">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center tracking-tight">What This Service Includes</h2>
            <div className="flex flex-wrap justify-center gap-6">
              {includes.map((item, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] p-8 rounded-3xl glass-card border-white/5 hover:border-red-500/20 transition-all group flex flex-col items-center text-center"
                >
                  <div className="w-14 h-14 rounded-xl bg-red-500/10 flex items-center justify-center text-red-400 mb-6 group-hover:scale-110 transition-transform">
                    <SafeIcon icon={item.icon} className="text-2xl" />
                  </div>
                  <h4 className="text-white font-bold text-xl mb-4">{item.title}</h4>
                  <p className="text-slate-400 leading-relaxed text-lg">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Why Quora Works */}
          <div className="max-w-4xl mx-auto">
            <div className="glass-card rounded-[3rem] p-12 md:p-20 border-red-500/20 bg-gradient-to-br from-slate-900/90 to-red-900/10 text-center">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-10 tracking-tight">Why Quora Works</h2>
              <p className="text-xl md:text-2xl text-slate-400 mb-12 leading-relaxed max-w-2xl mx-auto">
                Quora answers often rank in Google search results, allowing your brand to appear beyond the platform itself.
              </p>
              <div className="flex justify-center">
                <ul className="space-y-6 text-left inline-flex flex-col">
                  {benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-5 text-slate-300 text-xl">
                      <SafeIcon icon={FiIcons.FiCheckCircle} className="text-red-400 text-2xl flex-shrink-0" />
                      <span className="font-medium">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agency Section */}
      <section className="py-24 bg-slate-900/30 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Built for Agencies and Brands</h2>
            <p className="text-lg text-slate-400 leading-relaxed">
              PRBoost’s Quora service is designed for scalability and ease of use, making it the perfect addition to your agency's SEO and content stack.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <div className="space-y-8">
              {[
                { title: "Agency Dashboards", desc: "Manage multiple campaigns and clients with ease.", icon: FiIcons.FiUsers },
                { title: "Strategic Alignment", desc: "Align Quora answers with your existing SEO and content roadmap.", icon: FiIcons.FiTarget },
                { title: "Niche Scalability", desc: "Scale visibility across different niches and industries.", icon: FiIcons.FiTrendingUp }
              ].map((item, i) => (
                <div key={i} className="flex gap-6 p-6 rounded-2xl glass-card border-white/5">
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center text-red-400 flex-shrink-0">
                    <SafeIcon icon={item.icon} className="text-xl" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-lg mb-1">{item.title}</h4>
                    <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="w-full">
              <DialogueVisual />
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-24 tracking-tight">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-16 relative max-w-6xl mx-auto">
            {steps.map((step, i) => (
              <div key={i} className="relative flex flex-col items-center group">
                <div className="w-20 h-20 rounded-[2rem] bg-red-600 flex items-center justify-center text-white text-3xl font-bold mb-8 shadow-[0_0_40px_rgba(185,43,39,0.3)] group-hover:scale-110 transition-transform">
                  {step.num}
                </div>
                <h4 className="text-2xl font-bold text-white mb-4">{step.title}</h4>
                <p className="text-slate-400 text-lg leading-relaxed max-w-[280px]">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Last Section - H2 & Updated Button */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-red-600/5 blur-[120px] rounded-full"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-7xl font-extrabold text-white mb-8 tracking-tighter">
            Start Reaching Your <span className="text-red-500 text-gradient">Target Audience</span>
          </h2>
          <p className="text-xl md:text-2xl text-slate-400 mb-12 max-w-3xl mx-auto leading-relaxed">
            Quora provides a unique opportunity to connect with users actively searching for solutions. Drive consistent traffic and build long-term visibility.
          </p>
          <div className="flex justify-center">
            <Button href="#/contact" variant="primary" className="!from-red-600 !to-rose-600 !px-16 !py-6 text-2xl font-bold shadow-[0_0_60px_rgba(185,43,39,0.3)]">
              Contact us
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuoraService;
